import { useState } from 'react';
import { Alert, ErrorAlert, formatTime, Icon, relativeTime, Spinner, useApi, useAsyncAction, withStepUp } from '@pryme/ui';
import { CopyButton } from '@pryme/ui';
import { ErrorBox } from '../components/ErrorBox';
import { KeyDiagram } from '../components/KeyDiagram';
import { OneTimeSecretPanel } from '../components/OneTimeSecretPanel';
import { SecretKeyInput } from '../components/SecretKeyInput';
import { useServiceOutlet } from '../components/ServiceLayout';
import { TraceView } from '../components/TraceView';
import { matchSecret, prymeKeysOf, signedServiceRequest, type SignedTrace } from '../lib/signing';
import { canAdmin, type CredentialPanel, type ServiceKey } from '../lib/types';

/** One secret key = one key generation: your signing key plus Pryme's verification key for it (ADR-0013). */
interface SecretKeyRow {
  generation: string;
  signing?: ServiceKey;
  verification?: ServiceKey;
  status: 'active' | 'revoked';
  created_at: string;
  revoked_at: string | null;
}

function secretKeysOf(keys: ServiceKey[]): SecretKeyRow[] {
  const byGen = new Map<string, SecretKeyRow>();
  for (const k of keys) {
    const g = k.generation ?? k.id;
    const row = byGen.get(g) ?? { generation: g, status: 'revoked', created_at: k.created_at, revoked_at: null };
    if (k.direction === 'B') row.signing = k;
    else row.verification = k;
    if (k.status === 'active') row.status = 'active';
    if (k.created_at < row.created_at) row.created_at = k.created_at;
    if (k.revoked_at && (!row.revoked_at || k.revoked_at > row.revoked_at)) row.revoked_at = k.revoked_at;
    byGen.set(g, row);
  }
  // A generation counts as active only while both halves are.
  for (const r of byGen.values()) if (r.signing?.status !== 'active' || r.verification?.status !== 'active') r.status = 'revoked';
  return [...byGen.values()].sort((a, b) => (a.status === b.status ? b.created_at.localeCompare(a.created_at) : a.status === 'active' ? -1 : 1));
}

function SecretKeyRowView(props: { r: SecretKeyRow; newest: boolean; canRevoke: boolean; onRevoke(r: SecretKeyRow): void; busy: boolean }) {
  const { r } = props;
  const [confirm, setConfirm] = useState(false);
  const id = r.signing?.id ?? r.verification?.id ?? r.generation;
  return (
    <tr>
      <td>
        <code>{id}</code>
        {r.status === 'active' && props.newest && <div className="p-small p-muted">Newest</div>}
      </td>
      <td className="p-small">
        {r.signing && <div>Signing key <code className="p-mono-chip">{r.signing.fingerprint}</code></div>}
        {r.verification && <div>Pryme verification key <code className="p-mono-chip">{r.verification.id}</code> <CopyButton value={r.verification.public_key} label="Copy" /></div>}
      </td>
      <td><span className={`p-status ${r.status === 'active' ? 'ok' : 'bad'}`}>{r.status === 'active' ? 'Active' : 'Revoked'}</span>{r.revoked_at && <div className="p-small p-muted">{formatTime(r.revoked_at)}</div>}</td>
      <td title={formatTime(r.created_at)}>{relativeTime(r.created_at)}</td>
      <td title={formatTime(r.signing?.last_used_at ?? null)}>{relativeTime(r.signing?.last_used_at ?? null)}</td>
      <td>
        {props.canRevoke && r.status === 'active' && (confirm ? (
          <div className="p-row">
            <button type="button" className="p-btn danger small" disabled={props.busy} onClick={() => props.onRevoke(r)}>Confirm revoke</button>
            <button type="button" className="p-btn ghost small" onClick={() => setConfirm(false)}>Cancel</button>
          </div>
        ) : (
          <button type="button" className="p-btn danger-outline small" onClick={() => setConfirm(true)}>Revoke…</button>
        ))}
      </td>
    </tr>
  );
}

export function Credentials() {
  const { service: s, reload } = useServiceOutlet();
  const [panel, setPanel] = useState<CredentialPanel | null>(null);
  const [rotateMode, setRotateMode] = useState<null | 'overlap' | 'revoke'>(null);
  const gen = useAsyncAction();
  const revoke = useAsyncAction();
  const [revokedMsg, setRevokedMsg] = useState<string | null>(null);
  const audit = useApi<{ events: { sequence: number; event: string; detail: string; hash: string; at: string | number }[] }>(`/v1/services/${s.id}/key-audit`);

  const [secret, setSecret] = useState('');
  const [trace, setTrace] = useState<SignedTrace | null>(null);
  const test = useAsyncAction();
  const admin = canAdmin(s.role);

  const generate = async () => {
    const r = await gen.run(() => withStepUp<CredentialPanel>(`service.credentials:${s.id}`, `/v1/services/${s.id}/credentials`));
    if (r) setPanel(r);
  };
  const rotate = async (revokePrevious: boolean) => {
    const r = await gen.run(() => withStepUp<CredentialPanel>(`service.credentials:${s.id}`, `/v1/services/${s.id}/credentials/rotate`, { body: { revokePrevious } }));
    if (r) {
      setPanel(r);
      setRotateMode(null);
    }
  };
  const doRevoke = async (row: SecretKeyRow) => {
    setRevokedMsg(null);
    const kid = row.signing?.id ?? row.verification!.id;
    const r = await revoke.run(() => withStepUp<{ revoked: string[]; remainingActiveGenerations: number }>(`service.key.revoke:${kid}`, `/v1/services/${s.id}/keys/${kid}/revoke`));
    if (r) {
      setRevokedMsg(`Secret key ${kid} revoked. Requests signed with it now fail with credential_revoked, and Pryme no longer signs anything with its verification key.`);
      void reload();
      void audit.reload();
    }
  };
  const runTest = async () => {
    const m = matchSecret(secret, s.keys);
    if (!m.ok) return;
    setTrace(null);
    const r = await test.run(() =>
      signedServiceRequest({
        serviceId: s.id, keyId: m.key.id, secretKey: m.signingSecret, audience: s.apiAudience, path: '/v1/playground/echo',
        body: { test: 'signature', at: new Date().toISOString() }, prymeKeys: prymeKeysOf(s, m.key),
      }),
    );
    if (r) setTrace(r);
  };

  if (panel) {
    return (
      <OneTimeSecretPanel
        panel={panel}
        onAcknowledged={() => {
          setPanel(null);
          void reload();
          void audit.reload();
        }}
      />
    );
  }

  const secretKeys = secretKeysOf(s.keys);
  const active = secretKeys.filter((r) => r.status === 'active');
  const newestActive = active[0]?.generation;

  return (
    <>
      <section className="p-card" aria-labelledby="diag-h">
        <h2 id="diag-h">How your credentials work</h2>
        <KeyDiagram service={s} />
      </section>

      {s.status === 'pending_credentials' ? (
        <section className="p-card">
          <h2>Generate credentials</h2>
          <p className="p-small">Pryme generates a secret signing key for your backend and gives you Pryme's verification key. The secret key is shown once. Generating is confirmed with your passkey.</p>
          {s.redirectUris.length === 0 && <Alert tone="info">Tip: register redirect URIs too — activation requires at least one.</Alert>}
          <ErrorBox error={gen.error} />
          <button className="p-btn" disabled={!admin || gen.busy} onClick={generate}>{gen.busy ? <Spinner label="Confirm with your passkey…" /> : 'Generate credentials'}</button>
          {!admin && <p className="p-small p-muted">Only admins and owners can generate credentials.</p>}
        </section>
      ) : (
        <>
          <section className="p-card" aria-labelledby="keys-h">
            <h2 id="keys-h">Secret keys</h2>
            {revokedMsg && <Alert tone="ok">{revokedMsg}</Alert>}
            <ErrorBox error={revoke.error} />
            <div className="p-table-wrap" style={{ marginBottom: '0.75rem' }}>
              <table className="p-table">
                <thead><tr><th>Secret key</th><th>Contains</th><th>Status</th><th>Created</th><th>Last used</th><th><span className="p-skip">Actions</span></th></tr></thead>
                <tbody>{secretKeys.map((r) => <SecretKeyRowView key={r.generation} r={r} newest={active.length > 1 && r.generation === newestActive} canRevoke={admin} onRevoke={doRevoke} busy={revoke.busy} />)}</tbody>
              </table>
            </div>
            <p className="p-small p-muted" style={{ margin: 0 }}>
              Each secret key has its own signing key and its own Pryme verification key, so backends on different secret keys work independently. Revoking a secret key
              revokes both. Pryme never shows a secret again: only key IDs, fingerprints, public keys and last use are displayed.
            </p>
          </section>

          <section className="p-card" aria-labelledby="rot-h">
            <h2 id="rot-h">Rotate</h2>
            <div className="p-diagram">
              <div>
                <h3><Icon name="clock" size={16} />Planned rotation</h3>
                <p className="p-small">Issue a new secret key next to the current one, so you can deploy without downtime. Pryme answers each backend with the verification key of the secret it signs with, so old and new deployments keep working side by side. When every backend runs the new secret, revoke the old one. At most two secret keys are active at a time.</p>
                <button className="p-btn secondary" disabled={!admin || gen.busy || active.length >= 2} onClick={() => setRotateMode('overlap')}>Rotate (keep previous)</button>
                {active.length >= 2 && <p className="p-small p-muted" style={{ marginBottom: 0 }}>Two secret keys are active. Revoke the older one before rotating again.</p>}
              </div>
              <div>
                <h3><Icon name="alert" size={16} />Compromise response</h3>
                <p className="p-small">A secret that may have been exposed must be <strong>revoked</strong>, not only superseded. This issues a new secret key and revokes every previous one at once: requests signed with an old secret fail immediately, and Pryme stops signing with its verification keys.</p>
                <button className="p-btn danger-outline" disabled={!admin || gen.busy} onClick={() => setRotateMode('revoke')}>Rotate & revoke previous</button>
              </div>
            </div>
            {rotateMode && (
              <div className="p-alert warn" role="alertdialog" aria-label="Confirm rotation" style={{ marginTop: '1rem' }}>
                <p>
                  {rotateMode === 'revoke'
                    ? 'Every existing secret key will be revoked immediately. Your running backends will fail until they use the new secret. Continue?'
                    : 'A new secret key will be issued; the current one stays active until you revoke it. Continue?'}
                </p>
                <div className="p-row">
                  <button className={`p-btn small ${rotateMode === 'revoke' ? 'danger' : ''}`} disabled={gen.busy} onClick={() => rotate(rotateMode === 'revoke')}>
                    {gen.busy ? <Spinner label="Confirm with your passkey…" /> : 'Confirm with passkey'}
                  </button>
                  <button className="p-btn ghost small" onClick={() => setRotateMode(null)}>Cancel</button>
                </div>
              </div>
            )}
            <ErrorBox error={gen.error} />
          </section>

          <section className="p-card" aria-labelledby="test-h">
            <h2 id="test-h">Test signature</h2>
            <p className="p-small p-muted">Sign a request to <code>POST /v1/playground/echo</code> with your secret key. Pryme reports exactly what it verified, and this page checks Pryme’s signature on the response.</p>
            <SecretKeyInput value={secret} onChange={setSecret} keys={s.keys} />
            <button className="p-btn" disabled={test.busy || !matchSecret(secret, s.keys).ok} onClick={runTest}>
              {test.busy ? <Spinner label="Signing…" /> : 'Send signed test request'}
            </button>
            <ErrorAlert error={test.error} />
            {s.status !== 'active' && s.status !== 'pending_activation' && <Alert tone="warn">The echo only accepts active or pending-activation services.</Alert>}
            {trace && <div style={{ marginTop: '1rem' }}><TraceView trace={trace} /></div>}
          </section>
        </>
      )}

      <section className="p-card" aria-labelledby="aud-h">
        <h2 id="aud-h">Key audit log</h2>
        <p className="p-small p-muted">Hash-chained log kept by this service’s credential store. Changing or removing an entry breaks the chain.</p>
        <ErrorAlert error={audit.error} />
        {audit.data?.events.length ? (
          <div className="p-table-wrap">
            <table className="p-table">
              <thead><tr><th>#</th><th>Event</th><th>Detail</th><th>At</th><th>Hash</th></tr></thead>
              <tbody>
                {audit.data.events.map((e) => (
                  <tr key={e.sequence}>
                    <td>{e.sequence}</td>
                    <td><code>{e.event}</code></td>
                    <td className="p-small"><code>{typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail)}</code></td>
                    <td>{formatTime(typeof e.at === 'number' ? new Date(e.at * (e.at < 1e12 ? 1000 : 1)).toISOString() : e.at)}</td>
                    <td><code className="p-mono-chip">{String(e.hash).slice(0, 12)}…</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="p-muted">{audit.loading ? 'Loading…' : 'No key events yet.'}</p>
        )}
      </section>
    </>
  );
}
